import {
  DASHBOARD_PERMISSION_CODES,
  MATERIAL_PERMISSION_CODES,
  OBSERVATION_PERMISSION_CODES,
  SCHEDULE_PERMISSION_CODES,
  TEMPLATE_PERMISSION_CODES,
  USER_PERMISSION_CODES,
  hasPermission,
} from "../../utils/permissions";

// Primary navigation, split in two:
//   TOP_NAV_ITEMS — the daily-driver destinations shown directly in the sidebar.
//   ADMIN_ITEMS  — setup / management destinations. These are NOT shown in the
//                  sidebar individually; they live behind the single
//                  "Administration" hub item (see ADMIN_NAV_ITEM) so the rail
//                  stays short and never overflows. The /admin page renders them
//                  as cards, gated by the same per-item permission.
// `section` groups consecutive items under a label when the sidebar is expanded
// (divider when collapsed).
export const TOP_NAV_ITEMS = [
  {
    to: "/",
    end: true,
    labelKey: "nav.home",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
        />
      </svg>
    ),
  },
  {
    // Deliberately UNGATED. Every authenticated person is on one side of some
    // visit, the endpoint self-scopes, and the two permissions that look like
    // candidates both exclude somebody who belongs here: `dashboards.teacher.view`
    // hides it from evaluators and school admins, `observations…create` hides it
    // from the teachers whose lessons were observed.
    to: "/my-observations",
    labelKey: "nav.myObservations",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
        />
      </svg>
    ),
  },
  {
    to: "/my-materials",
    labelKey: "nav.learningMaterials",
    requiredPermission: DASHBOARD_PERMISSION_CODES.TEACHER_VIEW,
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
        />
      </svg>
    ),
  },
  {
    to: "/observation",
    labelKey: "nav.newObservation",
    accent: true,
    requiredPermission: OBSERVATION_PERMISSION_CODES.CREATE,
    icon: (
      <div className="w-6 h-6 bg-emerald-500 rounded-lg flex items-center justify-center text-white">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="3"
            d="M12 4v16m8-8H4"
          />
        </svg>
      </div>
    ),
  },
  {
    // First in the Analytics group, and not by taste: the other two answer "how
    // good are the lessons we saw", this one answers "whose lessons did nobody
    // see" — which is the question you have to ask before the scores mean
    // anything. Gated on SCHOOL_VIEW like its neighbours; the endpoint also
    // demands observations.observations.view_all, so a holder of one and not the
    // other still sees the item and lands on the page's own refusal rather than
    // on a silently missing rail entry.
    to: "/coverage",
    labelKey: "nav.coverage",
    section: "nav.sectionAnalytics",
    requiredPermission: DASHBOARD_PERMISSION_CODES.SCHOOL_VIEW,
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
        />
      </svg>
    ),
  },
  {
    to: "/standards",
    labelKey: "nav.standards",
    section: "nav.sectionAnalytics",
    requiredPermission: DASHBOARD_PERMISSION_CODES.SCHOOL_VIEW,
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
        />
      </svg>
    ),
  },
  {
    to: "/heatmap",
    labelKey: "nav.heatmap",
    section: "nav.sectionAnalytics",
    requiredPermission: DASHBOARD_PERMISSION_CODES.SCHOOL_VIEW,
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"
        />
      </svg>
    ),
  },
];

// Management destinations — surfaced as cards on the /admin hub, each gated by
// its own permission. Each carries a `descKey` for the hub card subtitle.
export const ADMIN_ITEMS = [
  {
    to: "/users",
    labelKey: "nav.manageUsers",
    descKey: "nav.manageUsersDesc",
    requiredPermission: USER_PERMISSION_CODES.MANAGE,
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
        />
      </svg>
    ),
  },
  // ONE card for the whole timetable feature. It used to be six — Schedule
  // Import, Timetable, Activities, Rooms, Constraints and Settings — which was
  // six of the nine cards on this hub for a single feature behind a single
  // permission code, and it turned building a timetable into a series of
  // round-trips back through here to reach the next step. They are tabs inside
  // /timetable now; the old routes redirect.
  {
    to: "/timetable",
    labelKey: "nav.timetable",
    descKey: "nav.timetableDesc",
    requiredPermission: SCHEDULE_PERMISSION_CODES.MANAGE,
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
    ),
  },
  {
    to: "/templates",
    labelKey: "nav.templates",
    descKey: "nav.templatesDesc",
    requiredPermission: TEMPLATE_PERMISSION_CODES.MANAGE,
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
        />
      </svg>
    ),
  },
  {
    to: "/materials",
    labelKey: "nav.materials",
    descKey: "nav.materialsDesc",
    requiredPermission: MATERIAL_PERMISSION_CODES.MANAGE,
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
        />
      </svg>
    ),
  },
];

// The single sidebar entry that opens the management hub. Sits last, separated
// from the Analytics group by a divider (`dividerBefore`) rather than a text
// header — a "Administration" header over an "Administration" item would just
// duplicate the label.
export const ADMIN_NAV_ITEM = {
  to: "/admin",
  labelKey: "nav.administration",
  dividerBefore: true,
  icon: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  ),
};

export const visibleTopNavItems = (user) =>
  TOP_NAV_ITEMS.filter(
    (item) => !item.requiredPermission || hasPermission(user, item.requiredPermission),
  );

export const visibleAdminItems = (user) =>
  ADMIN_ITEMS.filter((item) => hasPermission(user, item.requiredPermission));

// Sidebar registry: daily destinations + the Administration hub when the user
// can reach any management page.
export const visibleNavItems = (user) => {
  const items = visibleTopNavItems(user);
  return visibleAdminItems(user).length > 0 ? [...items, ADMIN_NAV_ITEM] : items;
};

// Launchpad (Home) registry for users without a dashboard: the daily items plus
// the management items flattened (no hub indirection on a page that is itself a
// card grid).
export const visibleLaunchpadItems = (user) => [
  ...visibleTopNavItems(user),
  ...visibleAdminItems(user),
];
